import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, isRefreshFor } from './AuthContext';
import { formatTimestamp } from './callUtils';
import { wallpaperProps } from './WhatsAppWallpaperPicker';
import { CreatePaymentLinkModal, formatAmount } from './PaymentLinks';
import {
  readSeq,
  mergeRows,
  applyStatuses,
  byMessageId,
  subscribeOutbox,
  getOutboxItems,
  enqueueOutbound,
  retryOutbound,
  discardOutbound,
  resolveOutbound,
  logTimingAtNextPaint,
} from './whatsappSync';
import {
  PaperclipIcon,
  FileIcon,
  CloseIcon,
  CheckIcon,
  SendIcon,
  EmojiIcon,
  BookmarkIcon,
  ImageIcon,
  VideoIcon,
  MicIcon,
  ReplyIcon,
  WalletIcon,
  NoteIcon,
  SearchIcon,
  ChevronDownIcon,
} from './Icons';

const IMAGE_ACCEPT = 'image/jpeg,image/png';
const VIDEO_ACCEPT = 'video/mp4,video/3gpp';
const DOCUMENT_ACCEPT = 'application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
const AUDIO_ACCEPT = 'audio/ogg,audio/mpeg,audio/mp4,audio/aac,audio/amr,.ogg,.opus,.mp3,.m4a,.aac,.amr';

const mediaTypeForFile = (file) => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'document';
};

// What the server's upload route accepts (WHATSAPP_ATTACHMENT_TYPES and the
// 16 MB limit in server/index.js). A pasted or dropped file is checked
// against these straight away, instead of failing only when sent.
const SENDABLE_TYPES_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  mp4: 'video/mp4',
  '3gp': 'video/3gpp',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  // Audio: WhatsApp voice notes are .ogg (Opus). Windows often labels .m4a
  // "audio/x-m4a", so the extension decides.
  ogg: 'audio/ogg',
  opus: 'audio/ogg',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  amr: 'audio/amr',
};
const SENDABLE_TYPES = new Set(Object.values(SENDABLE_TYPES_BY_EXTENSION));
const MAX_ATTACHMENT_BYTES = 16 * 1024 * 1024;

// WhatsApp only sends JPEG/PNG images, so another image format (WebP, GIF,
// BMP: common when copying from a website) is redrawn as a PNG.
const convertImageToPng = async (file) => {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0);
  if (bitmap.close) bitmap.close();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Image conversion failed');
  const base = (file.name || 'image').replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.png`, { type: 'image/png' });
};

// A pasted or dropped file, made sendable: { file } or { error }.
export const prepareAttachmentFile = async (file) => {
  const name = file.name || 'This file';
  let ready = file;
  if (!SENDABLE_TYPES.has(ready.type)) {
    // Some files arrive with no type (or a vague one); go by the extension.
    const extension = (file.name || '').split('.').pop().toLowerCase();
    const byExtension = SENDABLE_TYPES_BY_EXTENSION[extension];
    if (byExtension) {
      ready = new File([file], file.name, { type: byExtension });
    } else if (file.type.startsWith('image/')) {
      try {
        ready = await convertImageToPng(file);
      } catch {
        return { error: `Can't attach "${name}": WhatsApp only sends JPEG or PNG images.` };
      }
    } else {
      return {
        error: `Can't attach "${name}": WhatsApp only sends JPEG/PNG images, MP4/3GP videos, OGG/MP3/M4A/AAC/AMR audio and PDF, Word, Excel, PowerPoint or text files.`,
      };
    }
  }
  if (ready.size > MAX_ATTACHMENT_BYTES) return { error: `Can't attach "${name}": it's larger than 16 MB.` };
  return { file: ready };
};

const EMOJI_LIST = [
  '😀', '😁', '😂', '🤣', '😊', '😍', '😘', '😉', '😎', '🤔',
  '😢', '😭', '😡', '😱', '👍', '👎', '🙏', '👏', '💪', '🤝',
  '❤️', '🔥', '🎉', '✅', '❌', '⭐', '💯', '🙌', '👋', '🤷',
  '😴', '😅', '🥳', '😇', '🤗', '😐', '🙄', '😬', '👌', '✨',
  '📦', '🚚', '💰', '⏰', '📞', '📷', '🛒', '🎁', '⚠️', '💬',
];

// Closes an open popover on an outside mousedown or Escape — shared by the
// emoji grid and the canned-replies picker. No portal/scroll-listener logic
// needed (unlike client/src/SelectMenu.js) since both popovers are
// absolutely positioned inside the composer, which doesn't scroll on its own.
const useClosePopoverOnOutside = (open, containerRef, onClose) => {
  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      onClose();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, containerRef, onClose]);
};

// Current in-chat search term (see the search bar in WhatsAppChat below),
// read by LinkText so every rendered message body can <mark> its matches
// without the term being threaded through each bubble component.
const ChatSearchContext = createContext('');

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HighlightedText = ({ text, term }) => {
  if (!term) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, 'gi'));
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <mark key={index} className="whatsapp-search-mark">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

// Turns bare URLs inside a text message into clickable links — covers the
// "link preview" case from real WhatsApp (which auto-unfurls a URL found in
// plain text) without actually fetching/scraping the target page ourselves.
// Non-link text also gets the in-chat search term highlighted.
// WhatsApp's own inline formatting — *bold*, _italic_, ~strike~ — rendered
// the way the customer sees it, instead of showing the raw symbols. Same
// rule as WhatsApp: the markers must hug non-space text on one line and sit
// at word edges (so "order_no_123" stays as-is).
const WHATSAPP_FORMAT =
  /((?<![\p{L}\p{N}])(?:\*[^\s*](?:[^*\n]*[^\s*])?\*|_[^\s_](?:[^_\n]*[^\s_])?_|~[^\s~](?:[^~\n]*[^\s~])?~)(?![\p{L}\p{N}]))/gu;
const FORMAT_TAGS = { '*': 'strong', _: 'em', '~': 's' };

const FormattedText = ({ text, term }) =>
  String(text)
    .split(WHATSAPP_FORMAT)
    .map((part, index) => {
      if (index % 2 === 0) return <HighlightedText key={index} text={part} term={term} />;
      const Tag = FORMAT_TAGS[part[0]];
      return (
        <Tag key={index}>
          <HighlightedText text={part.slice(1, -1)} term={term} />
        </Tag>
      );
    });

const LinkText = ({ text }) => {
  const term = useContext(ChatSearchContext);
  const parts = String(text).split(/(https?:\/\/\S+)/g);
  return parts.map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <a key={index} href={part} target="_blank" rel="noreferrer" className="whatsapp-link-chip">
        {part}
      </a>
    ) : (
      <FormattedText key={index} text={part} term={term} />
    )
  );
};

// Text an in-chat search matches against for one message: its body plus
// whatever the rich card shows (interactive text, attachment name).
const searchableText = (message) =>
  [message.body, message.media_filename, message.rich?.body, message.rich?.text].filter(Boolean).join(' ').toLowerCase();

// One-line summary of a message for the quoted-reply preview — both the
// small block shown above the composer while composing a reply, and the
// small block shown inside a bubble that's replying to something. Same
// media-label convention as WhatsAppOverview.js's previewText.
const quotePreviewText = (message) => {
  switch (message.message_type) {
    case 'image':
      return '📷 Photo';
    case 'video':
      return '🎥 Video';
    case 'document':
      return `📎 ${message.media_filename || 'Document'}`;
    case 'audio':
    case 'voice':
      return '🎤 Audio';
    case 'reaction':
      return message.body ? `Reacted ${message.body}` : 'Reaction';
    case 'order':
      return message.body || '🛒 Cart';
    case 'contacts':
    case 'contact':
      return message.body || '👤 Contact';
    case 'location':
      return message.body || '📍 Location';
    case 'unsupported':
      return message.body || '⚠️ Unsupported message';
    default:
      return message.body || message.rich?.body || '(no text)';
  }
};

// Sent/delivered/read ticks for an outbound bubble, reusing the existing
// `status` column, which POST /api/webhook/whatsapp advances as AiSensy
// reports receipts: 'sent' = one grey tick, 'delivered' = two grey ticks,
// 'read' = two blue ticks, 'failed' = red "!".
// A reply this tab is still sending (see the outbox in whatsappSync.js) has
// no server status yet: it shows a clock, never a tick, until the server has
// actually accepted it.
const WhatsAppTicks = ({ message }) => {
  if (message.direction !== 'outbound') return null;
  if (message.localState === 'sending') {
    return (
      <span className="whatsapp-tick-pending" title="Sending…" aria-label="Sending">
        🕓
      </span>
    );
  }
  if (message.localState === 'failed') return <span className="whatsapp-tick-failed">!</span>;
  const status = (message.status || '').toLowerCase();
  if (status === 'failed') return <span className="whatsapp-tick-failed">!</span>;
  const doubleTick = status === 'delivered' || status === 'read';
  return (
    <span className={`whatsapp-ticks ${status === 'read' ? 'whatsapp-ticks-read' : ''}`}>
      <CheckIcon width={13} height={13} />
      {doubleTick && <CheckIcon width={13} height={13} className="whatsapp-tick-second" />}
    </span>
  );
};

const formatMoney = (amount, currency = 'INR') => {
  if (amount == null) return '—';
  const symbol = currency === 'INR' ? '₹' : `${currency} `;
  return `${symbol}${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const RichItemList = ({ items, currency }) =>
  items.length > 0 && (
    <ul className="whatsapp-rich-items">
      {items.map((item, index) => (
        <li key={index}>
          {item.image && <img src={item.image} alt="" className="whatsapp-rich-item-image" />}
          <span className="whatsapp-rich-item-name">{item.name || item.retailerId || 'Item'}</span>
          <span className="whatsapp-rich-item-qty">× {item.quantity}</span>
          {item.price != null && <span className="whatsapp-rich-item-price">{formatMoney(item.price, currency)}</span>}
        </li>
      ))}
    </ul>
  );

const RichButtons = ({ buttons }) =>
  buttons.length > 0 && (
    <div className="whatsapp-rich-buttons">
      {buttons.map((button, index) =>
        button.url ? (
          <a key={index} href={button.url} target="_blank" rel="noreferrer" className="whatsapp-rich-button">
            {button.title}
          </a>
        ) : (
          <span key={index} className="whatsapp-rich-button">
            {button.title}
          </span>
        )
      )}
    </div>
  );

// Cards for the message kinds server/whatsappRich.js parses out of the
// stored AiSensy payload — order (customer cart), interactive (buttons,
// lists, address request, order_details), a reply to one of those,
// contacts, location — plus a raw-data fallback for anything it couldn't.
const RichContent = ({ rich }) => {
  switch (rich.kind) {
    case 'order':
      return (
        <div className="whatsapp-rich-card">
          <div className="whatsapp-rich-title">🛒 Cart · {rich.items.reduce((sum, item) => sum + item.quantity, 0)} item(s)</div>
          <RichItemList items={rich.items} currency={rich.currency} />
          {rich.total != null && (
            <div className="whatsapp-rich-total">
              <span>Estimated total</span>
              <span>{formatMoney(rich.total, rich.currency)}</span>
            </div>
          )}
          {rich.text && <div className="whatsapp-bubble-caption"><LinkText text={rich.text} /></div>}
        </div>
      );
    case 'interactive': {
      const order = rich.order;
      return (
        <div className="whatsapp-rich-card">
          {rich.headerImage && <img src={rich.headerImage} alt="" className="whatsapp-media-image" />}
          {rich.header && <div className="whatsapp-rich-title">{rich.header}</div>}
          {order && (
            <div className="whatsapp-rich-order">
              <div className="whatsapp-rich-order-head">
                ORDER {order.referenceId ? `#${order.referenceId}` : ''}
                {order.status && <span className="whatsapp-rich-status">{order.status}</span>}
              </div>
              {order.description && <div className="whatsapp-rich-muted">{order.description}</div>}
              <RichItemList items={order.items} currency={order.currency} />
              {[
                ['Subtotal', order.subtotal],
                ['Discount', order.discount],
                ['Tax', order.tax],
                ['Shipping', order.shipping],
              ]
                .filter(([, value]) => value != null)
                .map(([label, value]) => (
                  <div key={label} className="whatsapp-rich-row">
                    <span>{label}</span>
                    <span>{formatMoney(value, order.currency)}</span>
                  </div>
                ))}
              {order.total != null && (
                <div className="whatsapp-rich-total">
                  <span>Total</span>
                  <span>{formatMoney(order.total, order.currency)}</span>
                </div>
              )}
            </div>
          )}
          {rich.body && <div className="whatsapp-bubble-body"><LinkText text={rich.body} /></div>}
          {rich.sections.map((section, index) => (
            <div key={index} className="whatsapp-rich-section">
              {section.title && <div className="whatsapp-rich-muted">{section.title}</div>}
              <ul className="whatsapp-rich-items">
                {section.rows.map((row, rowIndex) => (
                  <li key={rowIndex}>
                    <span className="whatsapp-rich-item-name">{row.title}</span>
                    {row.description && <span className="whatsapp-rich-muted">{row.description}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {rich.footer && <div className="whatsapp-rich-muted">{rich.footer}</div>}
          <RichButtons buttons={rich.buttons} />
        </div>
      );
    }
    case 'interactive_reply':
      return (
        <div className="whatsapp-rich-card">
          <div className="whatsapp-rich-reply">↩ {rich.title}</div>
          {rich.description && <div className="whatsapp-rich-muted">{rich.description}</div>}
          {rich.fields?.length > 0 && (
            <dl className="whatsapp-rich-fields">
              {rich.fields.map((field) => (
                <React.Fragment key={field.label}>
                  <dt>{field.label}</dt>
                  <dd>{field.value}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>
      );
    case 'contacts':
      return (
        <div className="whatsapp-rich-card">
          {rich.contacts.map((contact, index) => (
            <div key={index} className="whatsapp-rich-contact">
              <div className="whatsapp-rich-title">👤 {contact.name || 'Contact'}</div>
              {contact.org && <div className="whatsapp-rich-muted">{contact.org}</div>}
              {contact.phones.map((phone, phoneIndex) => (
                <a key={phoneIndex} href={`tel:${phone.phone.replace(/[^\d+]/g, '')}`} className="whatsapp-rich-link">
                  📞 {phone.phone}
                  {phone.type ? ` (${phone.type.toLowerCase()})` : ''}
                </a>
              ))}
              {contact.emails.map((email) => (
                <a key={email} href={`mailto:${email}`} className="whatsapp-rich-link">
                  ✉ {email}
                </a>
              ))}
            </div>
          ))}
        </div>
      );
    case 'location':
      return (
        <a
          className="whatsapp-rich-card whatsapp-rich-link"
          href={`https://www.google.com/maps?q=${rich.latitude},${rich.longitude}`}
          target="_blank"
          rel="noreferrer"
        >
          <div className="whatsapp-rich-title">📍 {rich.name || 'Location'}</div>
          {rich.address && <div className="whatsapp-rich-muted">{rich.address}</div>}
          <div className="whatsapp-rich-muted">
            {rich.latitude}, {rich.longitude} · Open in Maps
          </div>
        </a>
      );
    case 'unsupported':
      return (
        <div className="whatsapp-rich-card">
          <div className="whatsapp-rich-title">
            ⚠️ Unsupported message{rich.subtype ? ` (${rich.subtype.replace(/_/g, ' ')})` : ''}
          </div>
          <div className="whatsapp-rich-muted">
            The customer sent something WhatsApp doesn't pass on to business accounts, such as a poll, a
            view-once photo or video, an event or an edited message. Ask them to send it again as a normal
            message or photo.
          </div>
          {(rich.reason || rich.errorCode) && (
            <div className="whatsapp-rich-muted">
              {rich.reason}
              {rich.errorCode ? ` (error ${rich.errorCode})` : ''}
            </div>
          )}
        </div>
      );
    case 'raw':
      return (
        <details className="whatsapp-rich-card whatsapp-rich-raw">
          <summary>Message data from AiSensy</summary>
          <pre>{JSON.stringify(rich.data, null, 2)}</pre>
        </details>
      );
    default:
      return null;
  }
};

const WhatsAppBubbleContent = ({ message }) => {
  if (message.rich) return <RichContent rich={message.rich} />;
  switch (message.message_type) {
    case 'image':
    case 'sticker':
      return (
        <div className="whatsapp-media-card">
          {message.media_url ? (
            <a href={message.media_url} target="_blank" rel="noreferrer">
              <img src={message.media_url} alt={message.media_filename || 'attachment'} className="whatsapp-media-image" />
            </a>
          ) : (
            <div className="whatsapp-media-fallback">Image unavailable</div>
          )}
          {message.body && <div className="whatsapp-bubble-caption"><LinkText text={message.body} /></div>}
        </div>
      );
    case 'video':
      return (
        <div className="whatsapp-media-card">
          {message.media_url ? (
            <video src={message.media_url} controls className="whatsapp-media-video" />
          ) : (
            <div className="whatsapp-media-fallback">Video unavailable</div>
          )}
          {message.body && <div className="whatsapp-bubble-caption"><LinkText text={message.body} /></div>}
        </div>
      );
    case 'document':
      return (
        <a className="whatsapp-document-card" href={message.media_url || '#'} target="_blank" rel="noreferrer">
          <FileIcon />
          <div className="whatsapp-document-info">
            <span className="whatsapp-document-name">{message.media_filename || 'Document'}</span>
            <span className="whatsapp-document-meta">{message.media_mime_type || 'file'}</span>
          </div>
        </a>
      );
    case 'audio':
    case 'voice':
      return (
        <div className="whatsapp-media-card">
          {message.media_url ? (
            <audio src={message.media_url} controls className="whatsapp-media-audio" />
          ) : (
            <div className="whatsapp-media-fallback">Audio unavailable</div>
          )}
          {message.body && <div className="whatsapp-bubble-caption"><LinkText text={message.body} /></div>}
        </div>
      );
    case 'reaction':
      // The emoji itself is stashed in `body` (see the "reaction" comment in
      // extractWhatsappMessages, server/index.js) — not threaded onto
      // whichever earlier bubble it was reacting to, just shown as its own
      // small note in the thread.
      return (
        <div className="whatsapp-bubble-body whatsapp-bubble-reaction">
          {message.body ? <>Reacted {message.body}</> : <em>Reaction</em>}
        </div>
      );
    case 'text':
    case null:
    case undefined:
      return message.body ? (
        <div className="whatsapp-bubble-body"><LinkText text={message.body} /></div>
      ) : (
        <div className="whatsapp-bubble-body">
          <em>(no text)</em>
        </div>
      );
    default:
      // message_type is something we don't render (audio/video/sticker/
      // location/etc, or the media parser above found nothing usable) —
      // show what we know rather than a bare "(no text)".
      return (
        <div className="whatsapp-bubble-body whatsapp-bubble-unknown">
          {message.body ? <LinkText text={message.body} /> : <em>Unsupported {message.message_type} message</em>}
        </div>
      );
  }
};

// Payment-link status as shown on the chat bubble that carried the link.
const paymentStatusLabel = (link) => {
  switch (link.status) {
    case 'paid':
      return `✅ Paid · ${formatAmount(link.amount_paid || link.amount, link.currency)}`;
    case 'partially_paid':
      return `🟡 Partly paid · ${formatAmount(link.amount_paid, link.currency)} of ${formatAmount(link.amount, link.currency)}`;
    case 'expired':
      return '⌛ Link expired';
    case 'cancelled':
      return '✖ Link cancelled';
    default:
      return `⏳ Awaiting payment · ${formatAmount(link.amount, link.currency)}`;
  }
};

// An agent whose open chat checked in this recently counts as viewing it
// now (the open chat checks in on load and every FALLBACK_POLL_MS).
const VIEWING_NOW_MS = 45 * 1000;

const timeAgo = (iso) => {
  const diff = Date.now() - timeOf(iso);
  if (diff < 60 * 1000) return 'just now';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(timeOf(iso)).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

// Names joined naturally: "Bansi", "Bansi and Sweta", "Bansi, Sweta and Ravi".
const joinNames = (names) =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const unixToIso = (seconds) => (seconds ? new Date(Number(seconds) * 1000).toISOString() : null);
const timeOf = (iso) => {
  const parsed = Date.parse(iso || '');
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Safety-net poll while the panel is open, in case the SSE "refresh" event
// (see subscribeRefresh below) is ever missed — same reasoning as the 60s
// fallback in App.js and the 8s one in IncomingCallPopup.js.
const FALLBACK_POLL_MS = 15000;

// Only the newest part of a long thread is rendered; scrolling near the top
// (or "Show earlier messages") adds THREAD_PAGE more. Every keystroke makes
// the browser walk the whole page, so 2,000 rendered messages cost ~21 ms
// per key here and ~130 ms on a 6x slower PC. The whole thread is still
// loaded, searched and kept live; only the drawing is limited.
const THREAD_PAGE = 100;
const LOAD_EARLIER_WITHIN_PX = 300;

// The send route answers with the stored row as-is. Drop the server-only
// fields so it matches what a read returns (and a later read doesn't count
// as a change).
const cleanSavedRow = (row) => {
  // eslint-disable-next-line no-unused-vars
  const { raw_payload, alt_raw_payload, alt_wa_message_id, duplicate, updated, changed, ...message } = row;
  return { rich: null, ...message };
};

// A bubble for a reply that's still in the outbox (sending, or failed).
const outboxMessage = (item) => {
  const attachment = item.attachment;
  return {
    id: item.localId,
    direction: 'outbound',
    body: item.text || null,
    message_type: attachment ? attachment.mediaType : 'text',
    media_url: attachment ? attachment.previewUrl || attachment.url || null : null,
    media_filename: attachment ? (attachment.file ? attachment.file.name : attachment.filename) : null,
    media_mime_type: attachment ? (attachment.file ? attachment.file.type : attachment.mimeType) : null,
    reply_to_message_id: item.replyTo?.id ?? null,
    created_at: item.createdAt,
    sent_by: item.sentBy,
    status: null,
    rich: null,
    localState: item.state,
  };
};

const dayLabel = (isoString) => {
  if (!isoString) return null;
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

// The message thread (messages, notes, payment events, outbox bubbles).
// Memoized so typing in the composer, whose draft lives in WhatsAppChat,
// doesn't re-render every bubble on each keystroke: with 2,000 messages that
// took ~800 ms per key. Every prop is memoized or a ref in WhatsAppChat, and
// `actions` is one stable object, so this only re-renders when the thread,
// the search highlight or the quote-jump highlight actually changes.
const WhatsAppThread = React.memo(function WhatsAppThread({
  timeline,
  messagesById,
  paymentLinkByMessageId,
  highlightedId,
  searchMatchSet,
  currentMatchId,
  userId,
  userRole,
  wallpaper,
  bubbleRefs,
  threadEndRef,
  threadRef,
  hiddenCount,
  actions,
}) {
  let lastDay = null;
  return (
    <div
      ref={threadRef}
      className={`whatsapp-chat-thread ${wallpaper.className}`}
      style={wallpaper.style}
      onScroll={actions.onThreadScroll}
    >
      {hiddenCount > 0 && (
        <button type="button" className="link-btn whatsapp-load-earlier" onClick={actions.loadEarlier}>
          Show earlier messages ({hiddenCount.toLocaleString()})
        </button>
      )}
      {timeline.length === 0 && <p className="whatsapp-chat-empty">No WhatsApp messages yet.</p>}
      {timeline.map((item) => {
        const day = dayLabel(item.at);
        const showDayDivider = day && day !== lastDay;
        lastDay = day || lastDay;
        const divider = showDayDivider && <div className="chat-date-divider">{day}</div>;

        if (item.kind === 'note') {
          const { note } = item;
          const canDelete = note.author_id === userId || userRole === 'superadmin';
          return (
            <React.Fragment key={item.key}>
              {divider}
              <div className="whatsapp-note">
                <div className="whatsapp-note-head">
                  <span>🔒 Internal note · {note.author_name || 'Agent'}</span>
                  <span className="whatsapp-note-time">{formatTimestamp(note.created_at)}</span>
                  {canDelete && (
                    <button
                      type="button"
                      className="whatsapp-note-delete"
                      onClick={() => actions.deleteNote(note)}
                      aria-label="Delete note"
                      title="Delete note"
                    >
                      <CloseIcon width={12} height={12} />
                    </button>
                  )}
                </div>
                <div className="whatsapp-note-body">
                  <LinkText text={note.body} />
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (item.kind === 'pay-paid' || item.kind === 'pay-created') {
          const { link } = item;
          const paid = item.kind === 'pay-paid';
          return (
            <React.Fragment key={item.key}>
              {divider}
              <div className={`whatsapp-pay-event ${paid ? 'whatsapp-pay-event-paid' : ''}`}>
                {paid
                  ? `${link.status === 'partially_paid' ? '🟡 Partial payment received' : '✅ Payment received'} · ${formatAmount(
                      link.amount_paid || link.amount,
                      link.currency
                    )}`
                  : `💳 Payment link created · ${formatAmount(link.amount, link.currency)}`}
                {link.description && <span className="whatsapp-pay-event-desc"> · {link.description}</span>}
                <span className="whatsapp-pay-event-time"> · {formatTimestamp(item.at)}</span>
              </div>
            </React.Fragment>
          );
        }

        const { message, outboxItem } = item;
        const quoted = message.reply_to_message_id ? messagesById.get(message.reply_to_message_id) : null;
        const paymentLink = outboxItem ? null : paymentLinkByMessageId.get(message.id);
        return (
          <React.Fragment key={item.key}>
            {divider}
            <div
              className={`whatsapp-bubble-row whatsapp-bubble-row-${
                message.direction === 'outbound' ? 'outbound' : 'inbound'
              }`}
            >
              {/* No quoting a reply the server hasn't saved yet: it has no id to quote. */}
              {!outboxItem && (
                <button
                  type="button"
                  className="icon-btn whatsapp-reply-trigger"
                  onClick={() => actions.replyTo(message)}
                  aria-label="Reply"
                >
                  <ReplyIcon width={14} height={14} />
                </button>
              )}
              <div
                ref={(node) => {
                  if (node) bubbleRefs.current.set(message.id, node);
                  else bubbleRefs.current.delete(message.id);
                }}
                className={`whatsapp-bubble whatsapp-bubble-${
                  message.direction === 'outbound' ? 'outbound' : 'inbound'
                } ${highlightedId === message.id ? 'whatsapp-bubble-highlight' : ''} ${
                  searchMatchSet.has(message.id) ? 'whatsapp-bubble-search-match' : ''
                } ${currentMatchId === message.id ? 'whatsapp-bubble-search-current' : ''}`}
              >
                {quoted && (
                  <div className="whatsapp-quote-block" onClick={() => actions.scrollToMessage(quoted.id)}>
                    <span className="whatsapp-quote-sender">
                      {quoted.direction === 'outbound' ? quoted.sent_by || 'You' : 'Customer'}
                    </span>
                    <span className="whatsapp-quote-text">{quotePreviewText(quoted)}</span>
                  </div>
                )}
                <WhatsAppBubbleContent message={message} />
                {paymentLink && (
                  <div className={`whatsapp-pay-status whatsapp-pay-status-${paymentLink.status}`}>
                    {paymentStatusLabel(paymentLink)}
                  </div>
                )}
                <div className="whatsapp-bubble-meta">
                  {outboxItem?.state === 'sending' ? (
                    <span>Sending…</span>
                  ) : (
                    <span>{formatTimestamp(message.created_at)}</span>
                  )}
                  {message.sent_by && <span>· {message.sent_by}</span>}
                  <WhatsAppTicks message={message} />
                </div>
                {outboxItem?.state === 'failed' && (
                  <div className="whatsapp-outbox-failed" role="alert">
                    <span>Not sent{outboxItem.error ? `: ${outboxItem.error}` : ''}</span>
                    <button type="button" className="link-btn" onClick={() => actions.retry(outboxItem.localId)}>
                      Retry
                    </button>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => {
                        if (window.confirm('Remove this unsent message?')) discardOutbound(outboxItem.localId);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}
      <div ref={threadEndRef} />
    </div>
  );
});

// Two-way WhatsApp chat panel, shown inside the call-history modal alongside
// (not replacing) the existing "WhatsApp Greeting" template button — that
// button stays the only way to reach a customer outside the 24h session
// window this panel's free-text replies depend on.
// `searchOpen`/`onSearchClose` let a parent (WhatsAppOverview.js's 🔍
// button) open the in-chat search bar; without them the bar never shows.
function WhatsAppChat({
  customerNumber,
  customerName = null,
  focusMessage = null,
  // { text } from the customer info panel's "Send to customer" — dropped
  // into the composer (a new object each click, so repeats still apply).
  draftInsert = null,
  searchOpen = false,
  onSearchClose = () => {},
  // Optional DOM element (the WhatsApp page's header) to show "viewing now" /
  // "Seen by" in, so it doesn't take a row of its own above the thread.
  viewersSlot = null,
}) {
  const { authFetch, subscribeRefresh, user } = useAuth();
  // The logged-in agent's own chat background (WhatsAppWallpaperPicker.js).
  // Memoized: a new object would re-render the whole thread (WhatsAppThread)
  // on every keystroke.
  const chatWallpaper = user?.chatWallpaper;
  const wallpaper = useMemo(() => wallpaperProps(chatWallpaper), [chatWallpaper]);
  const [messages, setMessagesState] = useState([]);
  // Every change to `messages` goes through updateMessages: it's computed
  // once from the latest value here, not inside a React state updater, so
  // the merge bookkeeping (rowSeq) runs exactly once even in StrictMode.
  const messagesRef = useRef([]);
  const updateMessages = (compute) => {
    const next = compute(messagesRef.current);
    if (next === messagesRef.current) return;
    messagesRef.current = next;
    setMessagesState(next);
  };
  // Replies this tab has sent that the server hasn't confirmed yet
  // (whatsappSync.js's outbox), shown as "sending…" / "not sent" bubbles.
  const [outboxItems, setOutboxItems] = useState(() => getOutboxItems(customerNumber));
  const sendLockRef = useRef(false);
  // Agent-only notes on this thread (server: whatsapp_notes).
  const [notes, setNotes] = useState([]);
  // Other agents who've opened this chat (server: whatsapp_conversation_views).
  const [viewers, setViewers] = useState([]);
  // Razorpay links created for this customer (matched by phone), for the
  // "Payment received ✅" events and the status tag on the link's bubble.
  const [paymentLinks, setPaymentLinks] = useState([]);
  // 'reply' sends to the customer; 'note' saves an internal note instead.
  const [composeMode, setComposeMode] = useState('reply');
  const [sessionOpen, setSessionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState('');
  const [sendStatus, setSendStatus] = useState('idle'); // idle | sending | error
  const [sendError, setSendError] = useState('');
  // Staged file, not yet uploaded: { file, previewUrl, mediaType }. Cleared
  // on send or on manual remove.
  const [attachment, setAttachment] = useState(null);
  // The message the composer is currently quoting, or null. Set by a
  // bubble's hover "Reply" button, cleared on send or manual cancel.
  const [replyTarget, setReplyTarget] = useState(null);
  const threadEndRef = useRef(null);
  // DOM node per message id, so clicking a quoted-reply block can scroll the
  // original message into view (WhatsApp's own "jump to quoted message"
  // behavior) — populated via each bubble's ref callback below.
  const bubbleRefs = useRef(new Map());
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Cursor/selection to restore after a programmatic draft edit (formatting
  // wrap, emoji insert). A controlled textarea's DOM value only reflects the
  // new state once React commits the re-render, so this runs from a
  // useEffect keyed on `draft` (guaranteed post-commit) rather than
  // requestAnimationFrame (which can race the commit).
  const pendingSelectionRef = useRef(null);

  // 📎 attach menu (Document / Photos / Video), WhatsApp-Web style — one
  // button instead of a separate icon per attachment type.
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const attachMenuRef = useRef(null);
  useClosePopoverOnOutside(attachMenuOpen, attachMenuRef, () => setAttachMenuOpen(false));
  // Bold/italic/strike only show while text in the composer is selected
  // (again like WhatsApp), instead of taking permanent room in the row.
  const [hasSelection, setHasSelection] = useState(false);
  const updateSelectionState = () => {
    const el = textareaRef.current;
    setHasSelection(Boolean(el) && el.selectionStart !== el.selectionEnd);
  };

  // Razorpay payment link created from the 📎 menu — same form as the
  // Payment Links page, prefilled with this customer's number.
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);

  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const emojiPopoverRef = useRef(null);
  useClosePopoverOnOutside(emojiPickerOpen, emojiPopoverRef, () => setEmojiPickerOpen(false));

  const [cannedReplies, setCannedReplies] = useState([]);
  const [cannedPickerOpen, setCannedPickerOpen] = useState(false);
  const [cannedManageMode, setCannedManageMode] = useState(false);
  const cannedPopoverRef = useRef(null);
  useClosePopoverOnOutside(cannedPickerOpen, cannedPopoverRef, () => {
    setCannedPickerOpen(false);
    setCannedManageMode(false);
  });
  const cannedFilter = draft.startsWith('/') ? draft.slice(1).toLowerCase() : '';
  const filteredCannedReplies = cannedReplies.filter(
    (reply) =>
      reply.title.toLowerCase().includes(cannedFilter) ||
      (reply.shortcut || '').toLowerCase().includes(cannedFilter)
  );

  const [editingReplyId, setEditingReplyId] = useState(null); // null = "create" mode
  const [replyTitleDraft, setReplyTitleDraft] = useState('');
  const [replyShortcutDraft, setReplyShortcutDraft] = useState('');
  const [replyBodyDraft, setReplyBodyDraft] = useState('');
  // { file, previewUrl, mediaType } for a newly staged local file, or
  // { url, mediaType, filename, mimeType } for an existing reply's saved
  // media left untouched while editing. null = no attachment.
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [replySaving, setReplySaving] = useState(false);
  const [replyFormError, setReplyFormError] = useState('');
  const replyImageInputRef = useRef(null);
  const replyVideoInputRef = useRef(null);

  // Live sync for the open thread. One `sync` object per opened chat (a new
  // one when customerNumber changes), so answers that arrive for a chat
  // that's no longer open are dropped:
  //   customer   the thread
  //   rowSeq     seq each message row was last set from (whatsappSync.js)
  //   notesSeq   seq of the last note change applied from an event
  //   wantFull / ids / running   what still needs loading; one request at a
  //              time, so a burst of events becomes one or two requests
  //   eventAt    when the event being loaded arrived (timing log only)
  const syncRef = useRef(null);

  const fetchPaymentLinks = async (sync) => {
    try {
      const response = await authFetch(
        `/api/ivr/payment-links/for-customer/${encodeURIComponent(sync.customer)}`
      );
      const data = await response.json();
      if (response.ok && syncRef.current === sync) setPaymentLinks(Array.isArray(data.links) ? data.links : []);
    } catch {
      // No payment events shown if Razorpay is unreachable — the chat itself
      // still works.
    }
  };

  // Fire-and-forget: marks this thread read on every successful load (open,
  // every poll and every live update while it stays open), not just once —
  // see server/db.js's markWhatsappConversationRead. Also tells App.js's
  // sidebar badge to recount right away (the read route doesn't broadcast a
  // server-wide refresh).
  const markThreadRead = (sync) => {
    authFetch(`/api/whatsapp/conversations/${encodeURIComponent(sync.customer)}/read`, { method: 'POST' })
      .then(() => window.dispatchEvent(new Event('whatsapp-read')))
      .catch(() => {});
  };

  // The whole thread, plus notes, viewers and payment links. Used on open,
  // by the 15s safety-net poll, and whenever an event can't be applied on
  // its own. Only the first load shows the spinner.
  const loadFullThread = async (sync) => {
    const showSpinner = !sync.loadedOnce;
    if (showSpinner) setLoading(true);
    try {
      const response = await authFetch(`/api/customers/${encodeURIComponent(sync.customer)}/whatsapp-messages`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load messages');
      if (syncRef.current !== sync) return;
      const seq = readSeq(response);
      updateMessages((current) =>
        mergeRows(current, Array.isArray(data.messages) ? data.messages : [], {
          seq,
          full: true,
          rowSeq: sync.rowSeq,
          compare: byMessageId,
        })
      );
      // A note deleted by an event after this answer was built stays gone.
      if (seq === null || sync.notesSeq === null || seq >= sync.notesSeq) {
        setNotes(Array.isArray(data.notes) ? data.notes : []);
      }
      setViewers(Array.isArray(data.viewers) ? data.viewers : []);
      setSessionOpen(Boolean(data.sessionOpen));
      // Not awaited: payment status is a live Razorpay read (cached ~30s
      // server-side) and shouldn't hold up the thread itself.
      fetchPaymentLinks(sync);
      setLoadError('');
      markThreadRead(sync);
    } catch (error) {
      if (syncRef.current === sync) setLoadError(error.message || 'Failed to load messages');
    } finally {
      sync.loadedOnce = true;
      if (showSpinner && syncRef.current === sync) setLoading(false);
    }
  };

  // Only the rows an event named (a new message, new media, a status this
  // tab hadn't seen the row for).
  const loadThreadRows = async (sync, ids) => {
    try {
      const response = await authFetch(
        `/api/customers/${encodeURIComponent(sync.customer)}/whatsapp-messages?ids=${ids.join(',')}`
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load messages');
      if (syncRef.current !== sync) return;
      updateMessages((current) =>
        mergeRows(current, Array.isArray(data.messages) ? data.messages : [], {
          seq: readSeq(response),
          rowSeq: sync.rowSeq,
          compare: byMessageId,
        })
      );
      setSessionOpen(Boolean(data.sessionOpen));
      if (sync.eventAt !== null) {
        logTimingAtNextPaint('live event → message shown', sync.eventAt);
        sync.eventAt = null;
      }
      markThreadRead(sync);
    } catch {
      // Nothing shown: the next event or the 15s full reload catches up.
    }
  };

  // Queues a load and runs the queue if it isn't running already.
  const requestSync = (sync, { full = false, ids = [] } = {}) => {
    if (syncRef.current !== sync) return;
    if (full) sync.wantFull = true;
    ids.forEach((id) => sync.ids.add(id));
    if (sync.running) return;
    sync.running = true;
    (async () => {
      try {
        while (syncRef.current === sync && (sync.wantFull || sync.ids.size)) {
          if (sync.wantFull) {
            // The full thread covers any single rows asked for before it.
            sync.wantFull = false;
            sync.ids.clear();
            await loadFullThread(sync);
          } else {
            const ids = [...sync.ids];
            sync.ids.clear();
            await loadThreadRows(sync, ids);
          }
        }
      } finally {
        sync.running = false;
      }
    })();
  };

  // A 'refresh' event from the server. Events for another chat are ignored;
  // ticks are applied straight from the event; new or changed rows are
  // loaded by id. Anything without the details (an older server) or of a
  // kind not handled here reloads the whole thread, as before.
  const handleLiveEvent = (sync, event) => {
    if (!isRefreshFor(event, 'whatsapp') || syncRef.current !== sync) return;
    if (!event?.type || !event.customerNumber) {
      requestSync(sync, { full: true });
      return;
    }
    if (event.customerNumber !== sync.customer) return;
    const seq = Number.isFinite(event.seq) ? event.seq : null;
    switch (event.type) {
      case 'conversation_state':
        return; // shown by the page around the chat, not in it
      case 'note_deleted':
        if (seq !== null) sync.notesSeq = Math.max(sync.notesSeq ?? seq, seq);
        setNotes((previous) => previous.filter((note) => note.id !== event.noteId));
        return;
      case 'message_created':
      case 'message_updated':
      case 'message_status': {
        const messageIds = Array.isArray(event.messageIds) ? event.messageIds : [];
        if (event.clientMessageId && messageIds.length) resolveOutbound(event.clientMessageId, messageIds[0]);
        let missing = [];
        if (Array.isArray(event.statuses) && event.statuses.length) {
          updateMessages((current) => {
            const result = applyStatuses(current, event.statuses, { seq, rowSeq: sync.rowSeq });
            missing = result.missing;
            return result.rows;
          });
        }
        // Skip rows this tab already has from something at least as new
        // (e.g. its own send's response).
        const toLoad = [...messageIds, ...missing].filter(
          (id) => seq === null || !(sync.rowSeq.get(id) >= seq)
        );
        if (toLoad.length) {
          if (sync.eventAt === null && typeof performance !== 'undefined') sync.eventAt = performance.now();
          requestSync(sync, { ids: toLoad });
        }
        return;
      }
      default:
        requestSync(sync, { full: true });
    }
  };

  useEffect(() => {
    if (!customerNumber) return undefined;
    const sync = {
      customer: customerNumber,
      rowSeq: new Map(),
      notesSeq: null,
      wantFull: false,
      ids: new Set(),
      running: false,
      loadedOnce: false,
      eventAt: null,
    };
    syncRef.current = sync;
    messagesRef.current = [];
    setMessagesState([]);
    setNotes([]);
    setViewers([]);
    setPaymentLinks([]);
    setComposeMode('reply');
    setWindowStart(null);
    requestSync(sync, { full: true });
    setReplyTarget(null);
    textareaRef.current?.focus();
    const unsubscribe = subscribeRefresh((event) => handleLiveEvent(sync, event));
    // Safety net, unchanged: a full reload every 15s in case an event is
    // missed (SSE reconnecting, a proxy holding it back, ...).
    const interval = setInterval(() => requestSync(sync, { full: true }), FALLBACK_POLL_MS);
    return () => {
      unsubscribe();
      clearInterval(interval);
      if (syncRef.current === sync) syncRef.current = null;
    };
    // eslint-disable-next-line
  }, [customerNumber]);

  // Outbox changes for this chat: show/hide "sending" bubbles, put a
  // confirmed reply into the thread, and surface a failed send.
  useEffect(() => {
    if (!customerNumber) return undefined;
    setOutboxItems([...getOutboxItems(customerNumber)]);
    return subscribeOutbox((event) => {
      if (event.customerNumber !== customerNumber) return;
      setOutboxItems([...getOutboxItems(customerNumber)]);
      const sync = syncRef.current;
      if (event.type === 'saved' && event.message && sync?.customer === customerNumber) {
        updateMessages((current) =>
          mergeRows(current, [cleanSavedRow(event.message)], { seq: event.seq, rowSeq: sync.rowSeq, compare: byMessageId })
        );
      }
      if (event.type === 'failed') {
        setSendStatus('error');
        setSendError(event.item.error || 'Send failed');
      }
    });
    // eslint-disable-next-line
  }, [customerNumber]);

  // Outbox bubbles still to show: once the real row is in the thread, its
  // "sending" bubble goes.
  const visibleOutbox = useMemo(() => {
    const shownIds = new Set(messages.map((message) => message.id));
    return outboxItems.filter((item) => !(item.serverId && shownIds.has(item.serverId)));
  }, [outboxItems, messages]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, notes.length, visibleOutbox.length]);

  // Keyed by id, so a bubble whose reply_to_message_id points at an earlier
  // message can render that message's preview inline without a fetch — the
  // full thread's already loaded.
  const messagesById = useMemo(() => new Map(messages.map((message) => [message.id, message])), [messages]);

  // Which message carried each payment link (its text contains the link's
  // short_url) — that bubble gets the live status tag.
  const paymentLinkByMessageId = useMemo(() => {
    const map = new Map();
    paymentLinks.forEach((link) => {
      if (!link.short_url) return;
      const carrier = messages.find((message) => message.body && message.body.includes(link.short_url));
      if (carrier) map.set(carrier.id, link);
    });
    return map;
  }, [messages, paymentLinks]);

  // Messages, internal notes and payment events in one time-ordered list.
  // A link that was never sent in this chat (e.g. made on the Payment Links
  // page) gets its own "Payment link created" event; every paid/partly
  // paid link gets a "Payment received" event at the time it was paid.
  const timeline = useMemo(() => {
    const items = messages.map((message) => ({ kind: 'message', key: `m${message.id}`, at: message.created_at, message }));
    notes.forEach((note) => items.push({ kind: 'note', key: `n${note.id}`, at: note.created_at, note }));
    const carriedUrls = new Set([...paymentLinkByMessageId.values()].map((link) => link.id));
    paymentLinks.forEach((link) => {
      if (!carriedUrls.has(link.id)) {
        items.push({ kind: 'pay-created', key: `pc${link.id}`, at: unixToIso(link.created_at), link });
      }
      if ((link.status === 'paid' || link.status === 'partially_paid') && link.paid_at) {
        items.push({ kind: 'pay-paid', key: `pp${link.id}`, at: unixToIso(link.paid_at), link });
      }
    });
    // Stable sort, so messages keep their stored order on equal timestamps.
    const sorted = items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => timeOf(a.item.at) - timeOf(b.item.at) || a.index - b.index)
      .map(({ item }) => item);
    // Replies not yet confirmed by the server go last, in the order they
    // were sent (their time is this computer's clock, not the server's).
    return sorted.concat(
      visibleOutbox.map((outboxItem) => ({
        kind: 'message',
        key: outboxItem.localId,
        at: outboxItem.createdAt,
        message: outboxMessage(outboxItem),
        outboxItem,
      }))
    );
  }, [messages, notes, paymentLinks, paymentLinkByMessageId, visibleOutbox]);

  // The rendered part of the timeline (see THREAD_PAGE): from `windowStart`
  // to the end. null = "the newest THREAD_PAGE"; it's pinned to an index once
  // the chat has loaded, so new messages add to the bottom without pushing
  // the oldest rendered one out.
  const [windowStart, setWindowStart] = useState(null);
  const start =
    windowStart === null ? Math.max(0, timeline.length - THREAD_PAGE) : Math.min(windowStart, timeline.length);
  useEffect(() => {
    if (windowStart === null && timeline.length > 0) setWindowStart(Math.max(0, timeline.length - THREAD_PAGE));
  }, [windowStart, timeline.length]);
  const shownTimeline = useMemo(() => (start > 0 ? timeline.slice(start) : timeline), [timeline, start]);

  // Renders older messages down to message `id` (plus a little context above
  // it) if it's above the rendered part. True if it had to.
  const revealMessage = (id) => {
    const index = timeline.findIndex((item) => item.kind === 'message' && item.message.id === id);
    if (index < 0 || index >= start) return false;
    setWindowStart(Math.max(0, index - 10));
    return true;
  };

  // "Show earlier messages" / scrolling near the top. The scroll position is
  // restored after the older messages are added, so the view doesn't jump.
  const threadRef = useRef(null);
  const prependScrollRef = useRef(null);
  const loadEarlier = () => {
    const el = threadRef.current;
    if (start === 0 || prependScrollRef.current) return;
    prependScrollRef.current = el ? { height: el.scrollHeight, top: el.scrollTop } : null;
    setWindowStart(Math.max(0, start - THREAD_PAGE));
  };
  useLayoutEffect(() => {
    const saved = prependScrollRef.current;
    prependScrollRef.current = null;
    const el = threadRef.current;
    if (saved && el) el.scrollTop = el.scrollHeight - saved.height + saved.top;
  }, [shownTimeline]);

  useEffect(() => {
    if (!draftInsert?.text) return;
    setComposeMode('reply');
    setDraft(draftInsert.text);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [draftInsert]);

  const deleteNote = async (note) => {
    if (!window.confirm('Delete this internal note?')) return;
    try {
      const response = await authFetch(`/api/whatsapp-notes/${note.id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete note');
      }
      setNotes((previous) => previous.filter((n) => n.id !== note.id));
    } catch (err) {
      window.alert(err.message || 'Failed to delete note');
    }
  };

  const [highlightedId, setHighlightedId] = useState(null);
  // A quoted message above the rendered part: scrolled to once it renders.
  const pendingJumpRef = useRef(null);
  const scrollToMessage = (id, { smooth = true } = {}) => {
    const node = bubbleRefs.current.get(id);
    if (!node) {
      if (revealMessage(id)) pendingJumpRef.current = id;
      return;
    }
    node.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
    setHighlightedId(id);
    setTimeout(() => setHighlightedId((current) => (current === id ? null : current)), 1200);
  };
  // Not smooth: older messages were just rendered above, so the target can
  // be thousands of pixels away.
  useEffect(() => {
    const id = pendingJumpRef.current;
    if (id === null || !bubbleRefs.current.get(id)) return;
    pendingJumpRef.current = null;
    scrollToMessage(id, { smooth: false });
    // eslint-disable-next-line
  }, [shownTimeline]);

  // Jump to a message picked from the WhatsApp page's message search, once
  // it's rendered. Each focus request is handled once, so later polls don't
  // keep yanking the thread back to it. `focusMessage` is a fresh { id }
  // object per click, so picking the same result twice still re-scrolls.
  // Longer highlight than a quote-click since the eye has further to travel.
  const handledFocusRef = useRef(null);
  useEffect(() => {
    if (!focusMessage || handledFocusRef.current === focusMessage) return;
    const node = bubbleRefs.current.get(focusMessage.id);
    if (!node) {
      // Loaded but above the rendered part: render it, then this runs again.
      revealMessage(focusMessage.id);
      return;
    }
    handledFocusRef.current = focusMessage;
    const { id } = focusMessage;
    node.scrollIntoView({ block: 'center' });
    setHighlightedId(id);
    setTimeout(() => setHighlightedId((current) => (current === id ? null : current)), 2500);
    // eslint-disable-next-line
  }, [focusMessage, messages, shownTimeline]);

  // In-chat search: every message whose text contains the term, oldest
  // first; `searchIndex` is the one currently scrolled to. A new term starts
  // on the most recent match (WhatsApp's own behaviour); ↑ goes older.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);
  const searchInputRef = useRef(null);
  const activeSearchTerm = searchOpen ? searchQuery.trim() : '';
  const searchMatchIds = useMemo(() => {
    const term = activeSearchTerm.toLowerCase();
    if (!term) return [];
    return messages.filter((message) => searchableText(message).includes(term)).map((message) => message.id);
  }, [messages, activeSearchTerm]);
  const searchMatchSet = useMemo(() => new Set(searchMatchIds), [searchMatchIds]);
  const currentMatchId = searchMatchIds.length ? searchMatchIds[Math.min(searchIndex, searchMatchIds.length - 1)] : null;

  useEffect(() => {
    setSearchIndex(Math.max(0, searchMatchIds.length - 1));
    // Only a new term resets position — a poll adding a message shouldn't.
    // eslint-disable-next-line
  }, [activeSearchTerm]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
    else setSearchQuery('');
  }, [searchOpen]);

  // Scrolls to each new current match once. A match above the rendered part
  // is rendered first, then scrolled to on the next run.
  const scrolledMatchRef = useRef(null);
  useEffect(() => {
    if (!currentMatchId) {
      scrolledMatchRef.current = null;
      return;
    }
    if (scrolledMatchRef.current === currentMatchId) return;
    const node = bubbleRefs.current.get(currentMatchId);
    if (!node) {
      revealMessage(currentMatchId);
      return;
    }
    scrolledMatchRef.current = currentMatchId;
    node.scrollIntoView({ block: 'center' });
    // eslint-disable-next-line
  }, [currentMatchId, shownTimeline]);

  // direction -1 = older match, +1 = newer; wraps around at either end.
  const stepSearch = (direction) => {
    if (!searchMatchIds.length) return;
    setSearchIndex((index) => (index + direction + searchMatchIds.length) % searchMatchIds.length);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      stepSearch(event.shiftKey ? 1 : -1);
    } else if (event.key === 'Escape') {
      onSearchClose();
    }
  };

  // Canned replies aren't customer-scoped, so this is a separate one-time
  // fetch rather than folded into the per-customer effect above (which would
  // needlessly refetch every time the panel reopens for a different customer).
  const fetchCannedReplies = () => {
    return authFetch('/api/canned-replies')
      .then((response) => response.json())
      .then((data) => setCannedReplies(Array.isArray(data) ? data : []))
      .catch((error) => console.error('Error fetching canned replies:', error));
  };

  useEffect(() => {
    fetchCannedReplies();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!pendingSelectionRef.current) return;
    const { start, end } = pendingSelectionRef.current;
    pendingSelectionRef.current = null;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(start, end);
  }, [draft]);

  // 📎 menu pick: checked and normalised like a pasted or dropped file (an
  // .m4a can arrive as "audio/x-m4a", which the upload would refuse).
  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // allow picking the same file again after removing it
    stageFiles(files);
  };

  // A file picked, pasted into the composer or dropped on the chat. WhatsApp
  // sends one file per message, so only the first is attached.
  const stageFiles = async (files) => {
    if (!files.length) return;
    if (composeMode === 'note') {
      setSendStatus('error');
      setSendError("Internal notes can't have attachments. Switch back to Reply to send a file.");
      return;
    }
    const result = await prepareAttachmentFile(files[0]);
    if (result.error) {
      setSendStatus('error');
      setSendError(result.error);
      return;
    }
    const file = result.file;
    const mediaType = mediaTypeForFile(file);
    const previewUrl = mediaType === 'document' ? null : URL.createObjectURL(file);
    setAttachment((previous) => {
      if (previous?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previous.previewUrl);
      return { file, previewUrl, mediaType };
    });
    if (files.length > 1) {
      setSendStatus('error');
      setSendError(`Attached "${file.name}" only: WhatsApp sends one file per message.`);
    } else {
      setSendStatus('idle');
      setSendError('');
    }
    textareaRef.current?.focus();
  };

  // Ctrl+V: a copied file or image (screenshot, "Copy image", a file copied
  // in Explorer) is attached. Anything with plain text pastes as text as
  // before, since copying from Word or Excel also puts a picture of the text
  // on the clipboard.
  const handlePaste = (event) => {
    const data = event.clipboardData;
    const files = Array.from(data?.files || []);
    if (!files.length || (data.getData && data.getData('text/plain'))) return;
    event.preventDefault();
    stageFiles(files);
  };

  // Drag and drop a file anywhere on the chat. The depth counter ignores the
  // enter/leave pairs fired while the pointer crosses child elements.
  const [dropActive, setDropActive] = useState(false);
  const dragDepthRef = useRef(0);
  const draggingFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');
  const dropHandlers = {
    onDragEnter: (event) => {
      if (!draggingFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setDropActive(true);
    },
    onDragOver: (event) => {
      if (!draggingFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (event) => {
      if (!draggingFiles(event)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDropActive(false);
    },
    onDrop: (event) => {
      if (!draggingFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setDropActive(false);
      stageFiles(Array.from(event.dataTransfer.files || []));
    },
  };

  const removeAttachment = () => {
    // Only ever revoke a local blob: preview — a canned reply's staged
    // attachment (attachment.url) points at a real https:// URL, which
    // revokeObjectURL would just silently no-op on, but skipping it here is
    // clearer about intent than relying on that.
    if (attachment?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment(null);
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (composeMode === 'note') {
      if (!text) return;
      setSendStatus('sending');
      setSendError('');
      try {
        const response = await authFetch(`/api/customers/${encodeURIComponent(customerNumber)}/whatsapp-notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: text }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to save note');
        setNotes((previous) => [...previous, data]);
        setDraft('');
        // Back to replying, so the agent's next message can't be saved as
        // a note by mistake (or vice versa).
        setComposeMode('reply');
        setSendStatus('idle');
        requestAnimationFrame(() => textareaRef.current?.focus());
      } catch (error) {
        setSendStatus('error');
        setSendError(error.message || 'Failed to save note');
      }
      return;
    }
    if (!text && !attachment) return;
    // Two Enter presses in the same moment must not send the message twice.
    if (sendLockRef.current) return;
    sendLockRef.current = true;
    requestAnimationFrame(() => {
      sendLockRef.current = false;
    });
    const startedAt = typeof performance !== 'undefined' ? performance.now() : 0;
    // The reply shows straight away as a "sending" bubble and the composer
    // is free for the next message; the outbox (whatsappSync.js) does the
    // upload and the send in the background, one at a time, in order. The
    // staged attachment now belongs to the outbox (it frees the preview), so
    // it's cleared here without removeAttachment's revoke.
    // WhatsApp audio can't have a caption, so text typed with an audio file
    // goes as its own message right after it (the outbox keeps the order).
    const outgoing =
      attachment?.mediaType === 'audio' && text
        ? [
            { text: '', attachment, replyTo: replyTarget },
            { text, attachment: null, replyTo: null },
          ]
        : [{ text, attachment, replyTo: replyTarget }];
    outgoing.forEach((message) =>
      enqueueOutbound({ customerNumber, ...message, sentBy: user?.username || null, send: sendOutboundItem })
    );
    setDraft('');
    setAttachment(null);
    setReplyTarget(null);
    setSendStatus('idle');
    setSendError('');
    logTimingAtNextPaint('send click → bubble shown', startedAt);
    // Without this, the textarea loses focus after a send and the agent has
    // to click back into it before typing the next reply.
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  // Sends one outbox item: uploads its file first if it has one (only once,
  // even if the send is retried), then posts it. Resolves to the saved row
  // and its seq; throws on any failure, which marks the bubble "not sent".
  const sendOutboundItem = async (item) => {
    let mediaFields = item.uploaded || {};
    if (!item.uploaded && item.attachment?.file) {
      const formData = new FormData();
      formData.append('file', item.attachment.file);
      const uploadResponse = await authFetch(
        `/api/customers/${encodeURIComponent(item.customerNumber)}/whatsapp-attachment`,
        { method: 'POST', body: formData }
      );
      const uploadData = await uploadResponse.json();
      if (!uploadResponse.ok) throw new Error(uploadData.error || 'Attachment upload failed');
      mediaFields = {
        mediaUrl: uploadData.url,
        mediaType: uploadData.mediaType,
        mediaFilename: uploadData.name,
        mediaMimeType: uploadData.content_type,
      };
      item.uploaded = mediaFields;
    } else if (!item.uploaded && item.attachment?.url) {
      // Came from a canned reply's saved media — already hosted, no
      // upload step needed.
      mediaFields = {
        mediaUrl: item.attachment.url,
        mediaType: item.attachment.mediaType,
        mediaFilename: item.attachment.filename,
        mediaMimeType: item.attachment.mimeType,
      };
    }

    const response = await authFetch(`/api/customers/${encodeURIComponent(item.customerNumber)}/whatsapp-messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        body: item.text,
        replyToMessageId: item.replyTo?.id || null,
        clientMessageId: item.localId,
        ...mediaFields,
      }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Send failed');
    return { message: data, seq: readSeq(response) };
  };

  const createPaymentLink = async (payload) => {
    const response = await authFetch('/api/ivr/payment-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to create payment link.');
    return body.link;
  };

  // The ready-to-send WhatsApp text for a created link.
  const paymentLinkMessage = (link) => {
    const firstName = (customerName || '').trim().split(/\s+/)[0];
    const lines = [
      `Hi${firstName ? ` ${firstName}` : ''} 👋`,
      '',
      `Please complete your payment of *${formatAmount(link.amount, link.currency)}*${
        link.description ? ` for ${link.description}` : ''
      } using this secure link:`,
      link.short_url,
      '',
      'Thank you! 🙏',
    ];
    return lines.join('\n');
  };

  const [paymentLinkSending, setPaymentLinkSending] = useState(false);
  const [paymentLinkError, setPaymentLinkError] = useState('');

  // "Send in chat": posts the message straight away (same route as the
  // composer), then closes the form.
  const sendPaymentLink = async (link) => {
    setPaymentLinkSending(true);
    setPaymentLinkError('');
    try {
      const response = await authFetch(`/api/customers/${encodeURIComponent(customerNumber)}/whatsapp-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: paymentLinkMessage(link) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Send failed');
      const sync = syncRef.current;
      if (sync) {
        updateMessages((current) =>
          mergeRows(current, [cleanSavedRow(data)], { seq: readSeq(response), rowSeq: sync.rowSeq, compare: byMessageId })
        );
        // So the new bubble gets its "awaiting payment" tag now, not on the
        // next 15s reload.
        fetchPaymentLinks(sync);
      }
      setPaymentLinkOpen(false);
    } catch (err) {
      setPaymentLinkError(err.message || 'Send failed');
    } finally {
      setPaymentLinkSending(false);
    }
  };

  // "Edit before sending": drops the message into the composer instead.
  const insertPaymentLink = (link) => {
    setDraft(paymentLinkMessage(link));
    setPaymentLinkOpen(false);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleKeyDown = (event) => {
    if ((event.ctrlKey || event.metaKey) && !event.altKey) {
      const key = event.key.toLowerCase();
      if (key === 'b' || key === 'i') {
        event.preventDefault();
        wrapSelection(key === 'b' ? '*' : '_', key === 'b' ? '*' : '_');
        return;
      }
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleDraftChange = (event) => {
    const value = event.target.value;
    setDraft(value);
    if (value.startsWith('/')) setCannedPickerOpen(true);
  };

  // Wraps the textarea's current selection (or inserts empty markers with
  // the cursor placed between them, if nothing's selected) with WhatsApp's
  // own markdown syntax.
  const wrapSelection = (before, after) => {
    const el = textareaRef.current;
    if (!el) return;
    const { selectionStart, selectionEnd, value } = el;
    const selected = value.slice(selectionStart, selectionEnd);
    const newValue = value.slice(0, selectionStart) + before + selected + after + value.slice(selectionEnd);
    const cursorStart = selectionStart + before.length;
    const cursorEnd = cursorStart + selected.length;
    pendingSelectionRef.current = { start: cursorStart, end: cursorEnd };
    setDraft(newValue);
  };

  const insertAtCursor = (text) => {
    const el = textareaRef.current;
    const start = el ? el.selectionStart : draft.length;
    const end = el ? el.selectionEnd : draft.length;
    const newValue = draft.slice(0, start) + text + draft.slice(end);
    const cursor = start + text.length;
    pendingSelectionRef.current = { start: cursor, end: cursor };
    setDraft(newValue);
  };

  const applyCannedReply = (reply) => {
    setDraft(reply.body);
    setCannedPickerOpen(false);
    setCannedManageMode(false);
    // Full replace, same as the body text above — whatever was staged
    // before is swapped for this reply's own saved media (or cleared, if it
    // has none).
    if (attachment?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment(
      reply.media_url
        ? {
            url: reply.media_url,
            mediaType: (reply.media_mime_type || '').startsWith('video/') ? 'video' : 'image',
            filename: reply.media_filename,
            mimeType: reply.media_mime_type,
            previewUrl: reply.media_url,
          }
        : null
    );
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const resetReplyForm = () => {
    setEditingReplyId(null);
    setReplyTitleDraft('');
    setReplyShortcutDraft('');
    setReplyBodyDraft('');
    if (replyAttachment?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(replyAttachment.previewUrl);
    setReplyAttachment(null);
    setReplyFormError('');
  };

  const startEditReply = (reply) => {
    setEditingReplyId(reply.id);
    setReplyTitleDraft(reply.title);
    setReplyShortcutDraft(reply.shortcut || '');
    setReplyBodyDraft(reply.body);
    setReplyAttachment(
      reply.media_url
        ? {
            url: reply.media_url,
            mediaType: (reply.media_mime_type || '').startsWith('video/') ? 'video' : 'image',
            filename: reply.media_filename,
            mimeType: reply.media_mime_type,
          }
        : null
    );
  };

  const handleReplyAttachmentChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (replyAttachment?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(replyAttachment.previewUrl);
    const mediaType = mediaTypeForFile(file);
    setReplyAttachment({ file, previewUrl: URL.createObjectURL(file), mediaType });
  };

  const removeReplyAttachment = () => {
    if (replyAttachment?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(replyAttachment.previewUrl);
    setReplyAttachment(null);
  };

  const saveCannedReply = async (event) => {
    event.preventDefault();
    setReplyFormError('');
    setReplySaving(true);
    try {
      let mediaFields = { mediaUrl: null, mediaMimeType: null, mediaFilename: null };
      if (replyAttachment?.file) {
        const formData = new FormData();
        formData.append('file', replyAttachment.file);
        const uploadResponse = await authFetch('/api/canned-replies/attachment', { method: 'POST', body: formData });
        const uploadData = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadData.error || 'Attachment upload failed');
        mediaFields = {
          mediaUrl: uploadData.url,
          mediaMimeType: uploadData.content_type,
          mediaFilename: uploadData.name,
        };
      } else if (replyAttachment?.url) {
        mediaFields = {
          mediaUrl: replyAttachment.url,
          mediaMimeType: replyAttachment.mimeType,
          mediaFilename: replyAttachment.filename,
        };
      }

      const url = editingReplyId ? `/api/canned-replies/${editingReplyId}` : '/api/canned-replies';
      const method = editingReplyId ? 'PUT' : 'POST';
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: replyTitleDraft,
          shortcut: replyShortcutDraft,
          body: replyBodyDraft,
          ...mediaFields,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save canned reply');
      resetReplyForm();
      await fetchCannedReplies();
    } catch (error) {
      setReplyFormError(error.message || 'Failed to save canned reply');
    } finally {
      setReplySaving(false);
    }
  };

  const deleteCannedReply = async (id, title) => {
    if (!window.confirm(`Delete canned reply "${title}"?`)) return;
    try {
      await authFetch(`/api/canned-replies/${id}`, { method: 'DELETE' });
      if (editingReplyId === id) resetReplyForm();
      await fetchCannedReplies();
    } catch (error) {
      console.error('Error deleting canned reply:', error);
    }
  };

  // One stable object for the thread's buttons, so passing it doesn't break
  // WhatsAppThread's memo. Each call goes through the ref to this render's
  // handlers, so none of them ever runs with stale state.
  const threadHandlersRef = useRef(null);
  threadHandlersRef.current = {
    deleteNote,
    scrollToMessage,
    replyTo: (message) => {
      setReplyTarget(message);
      textareaRef.current?.focus();
    },
    retry: (localId) => retryOutbound(localId, sendOutboundItem),
    loadEarlier,
  };
  const threadActions = useMemo(
    () => ({
      deleteNote: (note) => threadHandlersRef.current.deleteNote(note),
      scrollToMessage: (id) => threadHandlersRef.current.scrollToMessage(id),
      replyTo: (message) => threadHandlersRef.current.replyTo(message),
      retry: (localId) => threadHandlersRef.current.retry(localId),
      loadEarlier: () => threadHandlersRef.current.loadEarlier(),
      onThreadScroll: (event) => {
        if (event.currentTarget.scrollTop < LOAD_EARLIER_WITHIN_PX) threadHandlersRef.current.loadEarlier();
      },
    }),
    []
  );

  return (
    <ChatSearchContext.Provider value={activeSearchTerm}>
    <div className="whatsapp-chat" {...dropHandlers}>
      {dropActive && (
        <div className="whatsapp-drop-overlay" aria-hidden="true">
          <PaperclipIcon width={28} height={28} />
          <span>{composeMode === 'note' ? 'Switch to Reply to attach a file' : 'Drop to attach'}</span>
        </div>
      )}
      {searchOpen && (
        <div className="whatsapp-chat-search" role="search">
          <SearchIcon width={15} height={15} className="whatsapp-chat-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search in this chat"
            aria-label="Search in this chat"
          />
          <span className="whatsapp-chat-search-count" aria-live="polite">
            {activeSearchTerm
              ? searchMatchIds.length
                ? `${Math.min(searchIndex, searchMatchIds.length - 1) + 1} of ${searchMatchIds.length}`
                : 'No results'
              : ''}
          </span>
          <button
            type="button"
            className="icon-btn whatsapp-chat-search-up"
            onClick={() => stepSearch(-1)}
            disabled={!searchMatchIds.length}
            aria-label="Previous (older) match"
            title="Older (Enter)"
          >
            <ChevronDownIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => stepSearch(1)}
            disabled={!searchMatchIds.length}
            aria-label="Next (newer) match"
            title="Newer (Shift+Enter)"
          >
            <ChevronDownIcon width={16} height={16} />
          </button>
          <button type="button" className="icon-btn" onClick={onSearchClose} aria-label="Close search" title="Close (Esc)">
            <CloseIcon width={14} height={14} />
          </button>
        </div>
      )}
      {viewers.length > 0 &&
        (() => {
          const now = viewers.filter((view) => Date.now() - timeOf(view.viewed_at) < VIEWING_NOW_MS);
          const earlier = viewers.filter((view) => !now.includes(view)).slice(0, 3);
          const name = (view) => view.user_name || 'An agent';
          const seenBy = (
            <div className="whatsapp-seen-by" aria-live="polite">
              {now.length > 0 && (
                <span className="whatsapp-seen-by-now">
                  <span className="whatsapp-seen-by-dot" aria-hidden="true" />
                  {joinNames(now.map(name))} {now.length === 1 ? 'is' : 'are'} viewing now
                </span>
              )}
              {earlier.length > 0 && (
                <span className="whatsapp-seen-by-earlier">
                  👁 Seen by{' '}
                  {earlier.map((view, index) => (
                    <React.Fragment key={view.user_id}>
                      {index > 0 && ', '}
                      <strong>{name(view)}</strong> · {timeAgo(view.viewed_at)}
                    </React.Fragment>
                  ))}
                </span>
              )}
            </div>
          );
          return viewersSlot ? createPortal(seenBy, viewersSlot) : seenBy;
        })()}
      {!sessionOpen && (
        <p className="whatsapp-chat-hint">
          This customer hasn't messaged in the last 24 hours, so WhatsApp may reject a free-text reply — use the
          "WhatsApp Greeting" template button above instead, or try sending anyway once they've messaged in.
        </p>
      )}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : loadError ? (
        <div className="call-feedback call-feedback-error">{loadError}</div>
      ) : (
        <WhatsAppThread
          timeline={shownTimeline}
          hiddenCount={start}
          threadRef={threadRef}
          messagesById={messagesById}
          paymentLinkByMessageId={paymentLinkByMessageId}
          highlightedId={highlightedId}
          searchMatchSet={searchMatchSet}
          currentMatchId={currentMatchId}
          userId={user?.id}
          userRole={user?.role}
          wallpaper={wallpaper}
          bubbleRefs={bubbleRefs}
          threadEndRef={threadEndRef}
          actions={threadActions}
        />
      )}

      {replyTarget && (
        <div className="whatsapp-reply-preview">
          <ReplyIcon width={14} height={14} />
          <div className="whatsapp-reply-preview-body">
            <span className="whatsapp-reply-preview-sender">
              {replyTarget.direction === 'outbound' ? replyTarget.sent_by || 'You' : 'Customer'}
            </span>
            <span className="whatsapp-reply-preview-text">{quotePreviewText(replyTarget)}</span>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={() => setReplyTarget(null)}
            aria-label="Cancel reply"
          >
            <CloseIcon width={14} height={14} />
          </button>
        </div>
      )}

      {attachment && (
        <div className="whatsapp-attachment-preview">
          {attachment.mediaType === 'image' && attachment.previewUrl ? (
            <img src={attachment.previewUrl} alt="attachment preview" />
          ) : attachment.mediaType === 'video' && attachment.previewUrl ? (
            <video src={attachment.previewUrl} muted />
          ) : attachment.mediaType === 'audio' && attachment.previewUrl ? (
            <audio src={attachment.previewUrl} controls preload="metadata" />
          ) : (
            <FileIcon />
          )}
          <span className="whatsapp-attachment-name">{attachment.file ? attachment.file.name : attachment.filename}</span>
          <button type="button" className="icon-btn" onClick={removeAttachment} aria-label="Remove attachment">
            <CloseIcon width={14} height={14} />
          </button>
        </div>
      )}

      <div className={`whatsapp-composer ${composeMode === 'note' ? 'whatsapp-composer-note' : ''}`}>
        <input ref={imageInputRef} type="file" accept={IMAGE_ACCEPT} onChange={handleAttachmentChange} hidden />
        <input ref={videoInputRef} type="file" accept={VIDEO_ACCEPT} onChange={handleAttachmentChange} hidden />
        <input ref={documentInputRef} type="file" accept={DOCUMENT_ACCEPT} onChange={handleAttachmentChange} hidden />
        <input ref={audioInputRef} type="file" accept={AUDIO_ACCEPT} onChange={handleAttachmentChange} hidden />

        <div className="whatsapp-composer-input">
          {hasSelection && (
            <div className="whatsapp-format-bar" role="toolbar" aria-label="Format selected text">
              <button
                type="button"
                className="whatsapp-format-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => wrapSelection('*', '*')}
                aria-label="Bold"
                title="Bold (Ctrl+B)"
              >
                <b>B</b>
              </button>
              <button
                type="button"
                className="whatsapp-format-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => wrapSelection('_', '_')}
                aria-label="Italic"
                title="Italic (Ctrl+I)"
              >
                <i>I</i>
              </button>
              <button
                type="button"
                className="whatsapp-format-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => wrapSelection('~', '~')}
                aria-label="Strikethrough"
                title="Strikethrough"
              >
                <span className="whatsapp-format-strike">S</span>
              </button>
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onSelect={updateSelectionState}
            onBlur={() =>
              // A blurred textarea keeps its selection range, so hide the
              // format bar explicitly unless focus came straight back.
              setTimeout(() => {
                if (document.activeElement !== textareaRef.current) setHasSelection(false);
              }, 150)
            }
            placeholder={
              composeMode === 'note'
                ? 'Internal note — only agents see this'
                : attachment?.mediaType === 'audio'
                ? 'Audio has no caption: anything typed here is sent after it'
                : attachment
                ? 'Add a caption…'
                : 'Type a message  ·  "/" for canned replies'
            }
            rows={1}
            disabled={sendStatus === 'sending'}
          />
        </div>

        <div className="whatsapp-composer-toolbar">
          <div className="whatsapp-composer-toolbar-left">
            <div className="whatsapp-popover-anchor" ref={attachMenuRef}>
              <button
                type="button"
                className={`icon-btn whatsapp-attach-trigger ${attachMenuOpen ? 'whatsapp-attach-trigger-open' : ''}`}
                onClick={() => setAttachMenuOpen((open) => !open)}
                disabled={sendStatus === 'sending'}
                aria-label="Attach"
                aria-haspopup="menu"
                aria-expanded={attachMenuOpen}
                title="Attach (or paste / drag a file into the chat)"
              >
                <PaperclipIcon />
              </button>
              {attachMenuOpen && (
                <div className="whatsapp-popover whatsapp-attach-menu" role="menu">
                  {[
                    { label: 'Document', icon: <FileIcon width={17} height={17} />, tone: 'document', input: documentInputRef },
                    { label: 'Photos', icon: <ImageIcon width={17} height={17} />, tone: 'photo', input: imageInputRef },
                    { label: 'Video', icon: <VideoIcon width={17} height={17} />, tone: 'video', input: videoInputRef },
                    { label: 'Audio', icon: <MicIcon width={17} height={17} />, tone: 'audio', input: audioInputRef },
                  ].map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      role="menuitem"
                      className="whatsapp-attach-item"
                      onClick={() => {
                        setAttachMenuOpen(false);
                        item.input.current?.click();
                      }}
                    >
                      <span className={`whatsapp-attach-icon whatsapp-attach-icon-${item.tone}`}>{item.icon}</span>
                      {item.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    role="menuitem"
                    className="whatsapp-attach-item"
                    onClick={() => {
                      setAttachMenuOpen(false);
                      setPaymentLinkOpen(true);
                    }}
                  >
                    <span className="whatsapp-attach-icon whatsapp-attach-icon-payment">
                      <WalletIcon width={17} height={17} />
                    </span>
                    Payment link
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="whatsapp-attach-item"
                    onClick={() => {
                      setAttachMenuOpen(false);
                      setCannedPickerOpen(true);
                    }}
                  >
                    <span className="whatsapp-attach-icon whatsapp-attach-icon-canned">
                      <BookmarkIcon width={17} height={17} />
                    </span>
                    Canned reply
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className={`icon-btn whatsapp-note-toggle ${composeMode === 'note' ? 'whatsapp-note-toggle-active' : ''}`}
              onClick={() => {
                setComposeMode((mode) => (mode === 'note' ? 'reply' : 'note'));
                requestAnimationFrame(() => textareaRef.current?.focus());
              }}
              disabled={sendStatus === 'sending'}
              aria-pressed={composeMode === 'note'}
              aria-label="Internal note"
              title={composeMode === 'note' ? 'Back to replying to the customer' : 'Write an internal note (only agents see it)'}
            >
              <NoteIcon />
            </button>

            <div className="whatsapp-popover-anchor" ref={emojiPopoverRef}>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setEmojiPickerOpen((open) => !open)}
                disabled={sendStatus === 'sending'}
                aria-label="Insert emoji"
              >
                <EmojiIcon />
              </button>
              {emojiPickerOpen && (
                <div className="whatsapp-popover whatsapp-emoji-popover">
                  <div className="whatsapp-emoji-grid">
                    {EMOJI_LIST.map((emoji) => (
                      <button
                        type="button"
                        key={emoji}
                        onClick={() => {
                          insertAtCursor(emoji);
                          setEmojiPickerOpen(false);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>


            <div className="whatsapp-popover-anchor" ref={cannedPopoverRef}>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setCannedPickerOpen((open) => !open)}
                disabled={sendStatus === 'sending'}
                aria-label="Canned replies"
                title='Canned replies (or type "/")'
              >
                <BookmarkIcon />
              </button>
              {cannedPickerOpen && (
                <div className="whatsapp-popover whatsapp-canned-popover">
                  <div className="whatsapp-canned-popover-header">
                    <span>{cannedManageMode ? 'Manage canned replies' : 'Canned replies'}</span>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => {
                        setCannedManageMode((mode) => !mode);
                        resetReplyForm();
                      }}
                    >
                      {cannedManageMode ? 'Back' : 'Manage'}
                    </button>
                  </div>
                  {cannedManageMode ? (
                    <>
                      <form className="whatsapp-canned-form" onSubmit={saveCannedReply}>
                        <input
                          type="text"
                          placeholder="Title"
                          value={replyTitleDraft}
                          onChange={(event) => setReplyTitleDraft(event.target.value)}
                          required
                        />
                        <input
                          type="text"
                          placeholder="Shortcut, e.g. greet (optional)"
                          value={replyShortcutDraft}
                          onChange={(event) => setReplyShortcutDraft(event.target.value)}
                        />
                        <textarea
                          placeholder="Message"
                          rows={3}
                          value={replyBodyDraft}
                          onChange={(event) => setReplyBodyDraft(event.target.value)}
                          required
                        />
                        <input
                          ref={replyImageInputRef}
                          type="file"
                          accept={IMAGE_ACCEPT}
                          onChange={handleReplyAttachmentChange}
                          hidden
                        />
                        <input
                          ref={replyVideoInputRef}
                          type="file"
                          accept={VIDEO_ACCEPT}
                          onChange={handleReplyAttachmentChange}
                          hidden
                        />
                        <div className="whatsapp-canned-form-media">
                          <button type="button" className="icon-btn" onClick={() => replyImageInputRef.current?.click()} aria-label="Attach an image">
                            <ImageIcon width={15} height={15} />
                          </button>
                          <button type="button" className="icon-btn" onClick={() => replyVideoInputRef.current?.click()} aria-label="Attach a video">
                            <VideoIcon width={15} height={15} />
                          </button>
                          {replyAttachment && (
                            <div className="whatsapp-attachment-preview">
                              {replyAttachment.mediaType === 'image' ? (
                                <img src={replyAttachment.previewUrl || replyAttachment.url} alt="attachment preview" />
                              ) : (
                                <video src={replyAttachment.previewUrl || replyAttachment.url} muted />
                              )}
                              <span className="whatsapp-attachment-name">
                                {replyAttachment.file ? replyAttachment.file.name : replyAttachment.filename}
                              </span>
                              <button type="button" className="icon-btn" onClick={removeReplyAttachment} aria-label="Remove attachment">
                                <CloseIcon width={14} height={14} />
                              </button>
                            </div>
                          )}
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={replySaving}>
                          {replySaving ? 'Saving…' : editingReplyId ? 'Save' : 'Add'}
                        </button>
                        {editingReplyId && (
                          <button type="button" className="btn btn-secondary" onClick={resetReplyForm}>
                            Cancel
                          </button>
                        )}
                      </form>
                      {replyFormError && <div className="call-feedback call-feedback-error">{replyFormError}</div>}
                      <ul className="whatsapp-canned-manage-list">
                        {cannedReplies.map((reply) => (
                          <li key={reply.id}>
                            <span>
                              {reply.shortcut && <span className="whatsapp-canned-shortcut">/{reply.shortcut}</span>}
                              {reply.title}
                              {reply.media_url && (reply.media_mime_type || '').startsWith('video/') ? (
                                <VideoIcon width={13} height={13} />
                              ) : reply.media_url ? (
                                <ImageIcon width={13} height={13} />
                              ) : null}
                            </span>
                            <span>
                              <button type="button" className="link-btn" onClick={() => startEditReply(reply)}>
                                Edit
                              </button>
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() => deleteCannedReply(reply.id, reply.title)}
                              >
                                Delete
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <ul className="whatsapp-canned-list">
                      {filteredCannedReplies.length === 0 ? (
                        <li className="whatsapp-canned-empty">No matching canned replies.</li>
                      ) : (
                        filteredCannedReplies.map((reply) => (
                          <li
                            key={reply.id}
                            className="whatsapp-canned-list-item"
                            onClick={() => applyCannedReply(reply)}
                          >
                            <span className="whatsapp-canned-title">
                              {reply.shortcut && <span className="whatsapp-canned-shortcut">/{reply.shortcut}</span>}
                              {reply.title}
                              {reply.media_url && (reply.media_mime_type || '').startsWith('video/') ? (
                                <VideoIcon width={13} height={13} />
                              ) : reply.media_url ? (
                                <ImageIcon width={13} height={13} />
                              ) : null}
                            </span>
                            <span className="whatsapp-canned-body-preview">{reply.body}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary whatsapp-composer-send"
            onClick={handleSend}
            disabled={sendStatus === 'sending' || (!draft.trim() && !attachment)}
            aria-label={sendStatus === 'sending' ? 'Sending' : 'Send'}
            title="Send (Enter)"
          >
            {sendStatus === 'sending' ? (
              <span className="whatsapp-send-spinner" aria-hidden="true" />
            ) : (
              <SendIcon width={17} height={17} />
            )}
          </button>
        </div>
      </div>
      {sendStatus === 'error' && <span className="call-feedback call-feedback-error">{sendError}</span>}

      {paymentLinkOpen && (
        <CreatePaymentLinkModal
          initialPhone={customerNumber}
          onCreated={createPaymentLink}
          onClose={() => {
            setPaymentLinkOpen(false);
            setPaymentLinkError('');
          }}
          successActions={(link) => (
            <div className="whatsapp-paylink-actions">
              <div className="whatsapp-paylink-preview">{paymentLinkMessage(link)}</div>
              {!sessionOpen && (
                <p className="wa-info-muted">
                  This customer hasn't messaged in 24 hours, so WhatsApp may not deliver a free-text message.
                </p>
              )}
              {paymentLinkError && <div className="call-feedback call-feedback-error">{paymentLinkError}</div>}
              <div className="whatsapp-paylink-buttons">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => insertPaymentLink(link)}
                  disabled={paymentLinkSending}
                >
                  Edit before sending
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => sendPaymentLink(link)}
                  disabled={paymentLinkSending}
                >
                  {paymentLinkSending ? 'Sending…' : 'Send in chat'}
                </button>
              </div>
            </div>
          )}
        />
      )}
    </div>
    </ChatSearchContext.Provider>
  );
}

// Memoized: the WhatsApp page re-renders on every call event during a live
// call (App.js's `calls`), which the chat doesn't use.
export default React.memo(WhatsAppChat);
